import { View, TouchableOpacity } from 'react-native';
import { Text } from '@/components';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ArrowLeft, MapPin } from 'lucide-react-native';
import TrailMap from '@/components/TrailMap';
import { useTrailDetail } from '@/lib/queries/trails';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useUserLocation } from '@/hooks/useUserLocation';
import colors from '@/constants/colors';
import { Typography } from '@/constants/typography';
import LottieLoader from '@/components/LottieLoader';
import { calculateDistance, formatDistance } from '@/utils/distance';

export default function TrailMapScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { session } = useAuth();
  const { locationCoords } = useUserLocation();
  const trailId = typeof id === 'string' ? id : undefined;
  const trailQuery = useTrailDetail(trailId, session?.accessToken);
  const trail = trailQuery.data;
  const isLoading = trailQuery.isLoading;

  const coords = trail
    ? (trail.coordinates?.length
      ? trail.coordinates
      : (trail.path ?? [])
          .filter((p) => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
          .map((p) => ({ latitude: p[1], longitude: p[0] }))
      )
    : [];

  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.backgroundPrimary }}>
        <LottieLoader size={120} />
      </View>
    );
  }

  const startCoordinate = trail?.startLatitude != null && trail?.startLongitude != null
    ? { latitude: trail.startLatitude, longitude: trail.startLongitude }
    : (coords[0] ?? null);

  const distanceFromUser = locationCoords && startCoordinate
    ? calculateDistance(locationCoords, startCoordinate) * 1000
    : null;

  return (
    <View style={{ flex: 1, backgroundColor: colors.backgroundPrimary }}>
      <Stack.Screen options={{ headerShown: false }} />

      <View style={{ flex: 1 }}>
        <TrailMap
          style={{ flex: 1 }}
          initialRegion={{
            latitude: trail?.startLatitude ?? coords[0]?.latitude ?? 40.7128,
            longitude: trail?.startLongitude ?? coords[0]?.longitude ?? -74.006,
            latitudeDelta: 0.01,
            longitudeDelta: 0.01,
          }}
          scrollEnabled
          zoomEnabled
          coordinates={coords}
          showsUserLocation={!!locationCoords}
          userLocation={locationCoords ?? null}
          showStartMarker
          startCoordinate={startCoordinate}
        />

        <TouchableOpacity
          onPress={() => router.back()}
          style={{
            position: 'absolute',
            top: Math.max(insets.top + 12, 24),
            left: 16,
            width: 44,
            height: 44,
            borderRadius: 22,
            backgroundColor: colors.backgroundPrimary,
            borderWidth: 1,
            borderColor: colors.borderSubtle,
            justifyContent: 'center',
            alignItems: 'center',
            shadowColor: colors.backgroundSecondaryVarient,
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.15,
            shadowRadius: 6,
            elevation: 4,
          }}
        >
          <ArrowLeft size={24} color={colors.accentPrimary} />
        </TouchableOpacity>

        <View
          style={{
            position: 'absolute',
            left: 16,
            right: 16,
            bottom: Math.max(insets.bottom + 16, 24),
            backgroundColor: colors.backgroundPrimary,
            borderRadius: 16,
            padding: 14,
            borderWidth: 1,
            borderColor: colors.borderSubtle,
            shadowColor: colors.backgroundSecondaryVarient,
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.15,
            shadowRadius: 10,
            elevation: 4,
          }}
        >
          <Text style={{ ...Typography.h2(colors.textPrimary), fontSize: 16, marginBottom: 4 }}>
            {trail?.name || t('trailDetail.trailMap')}
          </Text>
          {distanceFromUser != null && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <MapPin size={14} color={colors.accentPrimary} />
              <Text style={{ ...Typography.caption(colors.textMuted), fontSize: 13 }}>
                {t('trailDetail.distanceFromYou', { distance: formatDistance(distanceFromUser) })}
              </Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}
